import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentPreviewModal } from '@/components/AttachmentPreviewModal';
import { Button } from '@/components/Button';
import { Snackbar } from '@/components/Snackbar';
import { CustomPeriodCard } from '@/pages/Overview/CustomPeriodCard';
import { useCalendarOverflowSpace } from '@/pages/Overview/hooks/useCalendarOverflowSpace';
import { useEvaluationReport } from '@/pages/Overview/hooks/useEvaluationReport';
import { useSelectedPeriod } from '@/pages/Overview/hooks/useSelectedPeriod';
import { isSamePeriod } from '@/pages/Overview/period';
import { PeriodCard } from '@/pages/Overview/PeriodCard';
import { ButtonVariant, Size, SnackbarVariant } from '@/types/components';
import { StudyYearPeriodType } from '@/types/overview';
import { toApiDate } from '@/utils/date';
import { revokeObjectUrlLater, triggerDownload } from '@/utils/file';

export const Report: React.FC = () => {
  const { t } = useTranslation();
  const { periods, studyYearPeriod, selectedPeriod, selectPeriod } =
    useSelectedPeriod();
  const { contentEndRef, calendarSpace, handleCalendarBottomChange } =
    useCalendarOverflowSpace();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [snackbar, setSnackbar] = useState<{
    message: string;
    variant: SnackbarVariant;
  }>();
  const title = t('overview.tabs.report');
  const isCustomPeriod = selectedPeriod.type === StudyYearPeriodType.Custom;
  const fileName = `${title} ${toApiDate(selectedPeriod.from)}_${toApiDate(selectedPeriod.to)}.pdf`;
  const { mutate: generateReport, isPending } = useEvaluationReport();

  const closePreview = () => {
    if (previewUrl) {
      revokeObjectUrlLater(previewUrl);
    }

    setPreviewUrl(undefined);
  };

  const handleGenerate = () =>
    generateReport(selectedPeriod, {
      onSuccess: setPreviewUrl,
      onError: () =>
        setSnackbar({
          message: t('overview.report.error'),
          variant: SnackbarVariant.Error,
        }),
    });

  const handleDownload = (url: string) => {
    triggerDownload(url, fileName);
    setSnackbar({
      message: t('home.newsDetail.documentDownloadedSnackbar'),
      variant: SnackbarVariant.Success,
    });
    closePreview();
  };

  return (
    <div className="flex flex-1 flex-col pt-1 md:flex-none md:px-6 md:py-6">
      <div className="flex flex-1 flex-col rounded-t-2xl bg-popover px-4 pt-4 pb-8 md:flex-none md:rounded-[20px] md:bg-card md:px-5 md:pt-6 md:pb-6">
        <h1 className="text-callout-16 text-foreground md:text-title-24">
          {title}
        </h1>
        <p className="text-body-13 mt-1 text-muted-foreground md:hidden">
          {t('overview.report.subtitle')}
        </p>

        <h2 className="mt-5 text-subhead-14 text-muted-foreground md:mt-6 md:text-callout-16 md:text-foreground">
          {t('overview.ratings.period')}
        </h2>
        <div className="mt-2 grid items-start gap-2 md:mt-4 md:grid-cols-2">
          {periods.map((period) => (
            <PeriodCard
              key={period.name}
              period={period}
              isSelected={isSamePeriod(period, selectedPeriod)}
              onSelect={selectPeriod}
            />
          ))}
          <CustomPeriodCard
            studyYearPeriod={studyYearPeriod}
            value={isCustomPeriod ? selectedPeriod : null}
            onChange={selectPeriod}
            onCalendarBottomChange={handleCalendarBottomChange}
          />
        </div>

        <Button
          variant={ButtonVariant.Primary}
          size={Size.Large}
          isLoading={isPending}
          className="mt-6 h-14 w-full md:mt-8 md:h-10 md:w-fit md:min-w-[146px] md:rounded-3xl"
          onClick={handleGenerate}
        >
          {t('overview.report.generate')}
        </Button>
      </div>

      <div>
        <div
          ref={contentEndRef}
          style={{ height: calendarSpace }}
          className="transition-[height] duration-300 ease-out"
        />
      </div>

      {previewUrl && (
        <AttachmentPreviewModal
          name={fileName}
          url={previewUrl}
          onClose={closePreview}
          onDownload={() => handleDownload(previewUrl)}
        />
      )}

      <Snackbar
        compact
        message={snackbar?.message ?? ''}
        isOpen={!!snackbar}
        variant={snackbar?.variant}
        onDismiss={() => setSnackbar(undefined)}
      />
    </div>
  );
};
